import { supabase } from '@/lib/supabase/client';

export interface ChannelMatchResult {
  exact: { name: string; id: number; originalInput: string }[];
  fuzzy: { name: string; id: number; originalInput: string; similarity: number }[];
  notFound: string[];
  totalInput: number;
  totalFound: number;
}

export interface Channel {
  id: number;
  name: string;
}

/**
 * Normalizes a channel name for comparison by:
 * - Converting to lowercase
 * - Removing extra whitespace
 * - Removing common punctuation
 * - Handling common abbreviations
 */
function normalizeChannelName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    // Remove common punctuation
    .replace(/[.,;:!?'"()[\]{}]/g, '')
    // Handle common channel abbreviations
    .replace(/\bhd\b/g, 'high definition')
    .replace(/\btv\b/g, 'television')
    .replace(/\bcnn\b/g, 'cable news network')
    .replace(/\bbbc\b/g, 'british broadcasting corporation')
    // Remove "the" at the beginning
    .replace(/^the\s+/, '')
    .trim();
}

/**
 * Calculates similarity between two strings using Levenshtein distance
 */
function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
}

/**
 * Calculates Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i += 1) {
    matrix[0][i] = i;
  }
  
  for (let j = 0; j <= str2.length; j += 1) {
    matrix[j][0] = j;
  }
  
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Parses input text and extracts individual channel names
 */
function parseChannelNames(input: string): string[] {
  if (!input.trim()) return [];
  
  // Split by both newlines and commas, then clean up
  return input
    .split(/[,\n]+/)
    .map(name => name.trim())
    .filter(name => name.length > 0)
    // Remove duplicates while preserving original case
    .filter((name, index, arr) => 
      arr.findIndex(n => n.toLowerCase() === name.toLowerCase()) === index
    );
}

/**
 * Matches channel names against database channels with fuzzy matching
 */
export async function matchChannelNames(
  inputText: string,
  providerId: number,
  versionId: number,
  regionId?: number | null
): Promise<ChannelMatchResult> {
  const inputNames = parseChannelNames(inputText);
  
  if (inputNames.length === 0) {
    return {
      exact: [],
      fuzzy: [],
      notFound: [],
      totalInput: 0,
      totalFound: 0
    };
  }

  // Fetch all channels for the provider/version
  let query = supabase
    .from('channels')
    .select('id, name')
    .eq('provider_id', providerId)
    .eq('version_id', versionId);

  // Add region filter if specified
  if (regionId !== null && regionId !== undefined) {
    // Note: Assuming channels might have region-specific data
    // If channels don't have direct region association, remove this filter
  }

  const { data: channels, error } = await query;

  if (error) {
    console.error('Error fetching channels for matching:', error);
    throw new Error('Failed to fetch channels for matching');
  }

  if (!channels || channels.length === 0) {
    return {
      exact: [],
      fuzzy: [],
      notFound: inputNames,
      totalInput: inputNames.length,
      totalFound: 0
    };
  }

  const exact: ChannelMatchResult['exact'] = [];
  const fuzzy: ChannelMatchResult['fuzzy'] = [];
  const notFound: string[] = [];

  // Create normalized lookup maps
  const channelNormalizedMap = new Map<string, Channel[]>();
  channels.forEach(channel => {
    const normalized = normalizeChannelName(channel.name);
    if (!channelNormalizedMap.has(normalized)) {
      channelNormalizedMap.set(normalized, []);
    }
    channelNormalizedMap.get(normalized)!.push(channel);
  });

  for (const inputName of inputNames) {
    const normalizedInput = normalizeChannelName(inputName);
    let matched = false;

    // First try exact match
    if (channelNormalizedMap.has(normalizedInput)) {
      const matchedChannels = channelNormalizedMap.get(normalizedInput)!;
      // Use the first match if multiple channels have the same normalized name
      const bestMatch = matchedChannels[0];
      exact.push({
        name: bestMatch.name,
        id: bestMatch.id,
        originalInput: inputName
      });
      matched = true;
      continue;
    }

    // If no exact match, try fuzzy matching
    let bestMatch: { channel: Channel; similarity: number } | null = null;
    const minSimilarity = 0.7; // 70% similarity threshold

    for (const channel of channels) {
      const normalizedChannel = normalizeChannelName(channel.name);
      const similarity = calculateSimilarity(normalizedInput, normalizedChannel);
      
      if (similarity >= minSimilarity && (!bestMatch || similarity > bestMatch.similarity)) {
        bestMatch = { channel, similarity };
      }
    }

    if (bestMatch) {
      fuzzy.push({
        name: bestMatch.channel.name,
        id: bestMatch.channel.id,
        originalInput: inputName,
        similarity: bestMatch.similarity
      });
      matched = true;
    }

    if (!matched) {
      notFound.push(inputName);
    }
  }

  // Sort fuzzy matches by similarity (highest first)
  fuzzy.sort((a, b) => b.similarity - a.similarity);

  return {
    exact,
    fuzzy,
    notFound,
    totalInput: inputNames.length,
    totalFound: exact.length + fuzzy.length
  };
}

/**
 * Bulk assigns plans to channels by their IDs
 */
export async function bulkAssignPlansByChannelIds(
  channelIds: number[],
  planId: number,
  versionId: number
): Promise<void> {
  if (channelIds.length === 0) {
    throw new Error('No channels provided for plan assignment');
  }

  // Verify the plan exists
  const { data: plan, error: planError } = await supabase
    .from('plans')
    .select('id')
    .eq('id', planId)
    .eq('version_id', versionId)
    .single();

  if (planError || !plan) {
    throw new Error('Selected plan does not exist or is not available for this version');
  }

  // Verify all channels exist and belong to the correct version
  const { data: existingChannels, error: channelError } = await supabase
    .from('channels')
    .select('id')
    .eq('version_id', versionId)
    .in('id', channelIds);

  if (channelError) {
    throw new Error('Failed to verify channels');
  }

  const existingChannelIds = existingChannels?.map(c => c.id) || [];
  const missingChannels = channelIds.filter(id => !existingChannelIds.includes(id));

  if (missingChannels.length > 0) {
    throw new Error(`Some channels do not exist or belong to this version: ${missingChannels.join(', ')}`);
  }

  // First, remove existing plan associations for these channels
  const { error: deleteError } = await supabase
    .from('channel_plans')
    .delete()
    .in('channel_id', channelIds)
    .eq('plan_id', planId)
    .eq('version_id', versionId);

  if (deleteError) {
    console.error('Error removing existing plan associations:', deleteError);
    throw new Error('Failed to remove existing plan associations');
  }

  // Then, create new plan associations
  const planAssignments = channelIds.map(channelId => ({
    channel_id: channelId,
    plan_id: planId,
    version_id: versionId
  }));

  const { error: insertError } = await supabase
    .from('channel_plans')
    .insert(planAssignments);

  if (insertError) {
    console.error('Error creating plan assignments:', insertError);
    throw new Error('Failed to assign plans to channels');
  }
}